import { groupBy, round, sumBy } from "lodash";

import { components } from "@/@types/api";

import {
  FunnelStageFilter,
  GeoViewToggle,
} from "../pages/scenario-results/components/ScenarioBreakdownTableOptions";

type Revenue = components["schemas"]["ScenarioResultRevenue"];
type Spend = components["schemas"]["ScenarioResultSpend"];

/**
 * Calculates ROAS (Return on Ad Spend) given spend and revenue.
 * Return `undefined` if spend is 0.
 */
export const calculateROI = ({ spend, revenue }: { spend: number; revenue: number }) => {
  if (spend === 0) {
    return;
  }

  return revenue / spend - 1;
};

type SpendAndRevenueItem = {
  spend: number;
  revenue: number;
  volume: number;
  type: "spend" | "revenue";
  channel: string;
  brand: string;
  category: string;
  product: string;
  geography?: string;
};

const getGroupByElements = (
  item: SpendAndRevenueItem,
  {
    funnelStageFilter,
    geoViewToggle,
  }: {
    funnelStageFilter: FunnelStageFilter;
    geoViewToggle: GeoViewToggle;
  }
) => {
  const geography = geoViewToggle === "split" && item.geography ? item.geography : undefined;
  const { brand, category, product } =
    funnelStageFilter === "split"
      ? item
      : {
          brand: undefined,
          category: undefined,
          product: undefined,
        };

  return {
    geography,
    brand,
    category,
    product,
    channel: item.channel,
  };
};

// For each channel, get the spend and revenue at the brand, category, and product level
export const getBreakdownTable = (
  revenues: Revenue[],
  spends: Spend[],
  funnelStageFilter: FunnelStageFilter,
  geoViewToggle: GeoViewToggle = "aggregate"
) => {
  const spendsAndRevenues = [
    ...revenues.map((revenue) => ({
      revenue: revenue.revenue,
      volume: revenue.volume,
      type: "revenue",
      channel: revenue.channel,
      brand: revenue.marketingSpendBrand,
      category: revenue.marketingSpendCategory,
      product: revenue.marketingSpendProduct,
      geography: revenue.geography,
    })),
    ...spends.map((spend) => ({
      spend: spend.spend,
      type: "spend",
      channel: spend.channel,
      brand: spend.marketingSpendBrand,
      category: spend.marketingSpendCategory,
      product: spend.marketingSpendProduct,
      geography: spend.geography,
    })),
  ];

  // Use a lookup map so we cater for when brand, category, product, geography, channel hae hyphens.
  const valueLookupMap = new Map<
    string,
    {
      channel: string;
      brand?: string;
      category?: string;
      product?: string;
      geography?: string;
    }
  >();

  const groupByFn = (item: SpendAndRevenueItem) => {
    const { geography, brand, category, product, channel } = getGroupByElements(item, {
      funnelStageFilter,
      geoViewToggle,
    });

    const key = `${channel}-${brand ?? ""}-${category ?? ""}-${product ?? ""}-${geography ?? ""}`;
    if (!valueLookupMap.has(key)) {
      valueLookupMap.set(key, {
        channel,
        brand,
        category,
        product,
        geography,
      });
    }
    return key;
  };

  const groupedByBusinessUnitChannel = groupBy(spendsAndRevenues, groupByFn);

  const summaryRows = Object.entries(groupedByBusinessUnitChannel).map(([key, items]) => {
    const lookupValue = valueLookupMap.get(key) ?? {
      channel: "",
      brand: undefined,
      category: undefined,
      product: undefined,
      geography: undefined,
    };
    const { channel, brand, category, product, geography } = lookupValue;

    const sumSpend = sumBy(items, "spend") ?? 0;
    const sumRevenue = sumBy(items, "revenue") ?? 0;
    const sumVolume = sumBy(items, "volume") ?? 0;
    // Default to 0 if ROI is undefined so we have proper sorting
    const roi = calculateROI({ spend: sumSpend, revenue: sumRevenue }) ?? 0;

    return {
      id: key,
      channel: channel,
      brand: brand || undefined,
      category: category || undefined,
      product: product || undefined,
      business_unit:
        brand && category && product
          ? {
              brand,
              category,
              product,
            }
          : undefined,
      geography: geography || undefined,
      spend: Math.floor(sumSpend),
      revenue: Math.floor(sumRevenue),
      volume: Math.floor(sumVolume),
      roi: round(roi, 2),
    };
  });

  return summaryRows.sort((a, b) => {
    const aValue = `${a.brand}-${a.category}-${a.product}-${a.channel}-${a.geography}`;
    const bValue = `${b.brand}-${b.category}-${b.product}-${b.channel}-${b.geography}`;
    return aValue.localeCompare(bValue);
  });
};
